import React, { useCallback, useEffect, useMemo, useState } from "react";
import { v4 as uuidv4 } from "uuid";

import useDataSourceManager from "hooks/useDataSourceManager";

import { ReactComponent as CheckedIcon } from "assets/images/icons/icn_checked.svg";
import { ReactComponent as UnCheckedIcon } from "assets/images/icons/icn_unchecked.svg";

import { OutlineRectangleButton } from "components/buttons/OutlineRectangleButton";
import { SolidButton } from "components/buttons/SolidRectangleButton";
import InputField from "components/InputField";
import { validateIgmpVlan } from "utils/validate";

import {
  Box,
  Typography,
  Divider,
  RadioGroup,
  Radio,
  FormControlLabel,
} from "@mui/material";

import { useTheme } from "@mui/material/styles";

const ConfigRow = ({ showEditGeneral, label, value, onChange }) => {
  const theme = useTheme();

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        mt: 2,
      }}
    >
      <Typography
        sx={{
          width: "200px",
          flexShrink: 0,
          color: theme.palette.primary.greyPrimary,
          fontSize: theme.typography.customFontSize[2],
          fontWeight: theme.typography.customFontWeight[1],
        }}
      >
        {label}
      </Typography>

      <RadioGroup
        row
        value={value ? "enable" : "disable"}
        onChange={(event) => {
          onChange(event.target.value === "enable");
        }}
      >
        {["Enable", "Disable"].map((option) => {
          const optionValue = option.toLowerCase();

          return (
            <FormControlLabel
              key={optionValue}
              value={optionValue}
              disabled={!showEditGeneral}
              control={
                <Radio
                  icon={<UnCheckedIcon />}
                  checkedIcon={<CheckedIcon />}
                  sx={{
                    "&:hover": {
                      backgroundColor: "transparent",
                    },
                  }}
                />
              }
              label={option}
              sx={{
                "& .MuiFormControlLabel-label": {
                  fontSize: theme.typography.customFontSize[2],
                  color: theme.palette.primary.greyPrimary,
                },

                "& .MuiFormControlLabel-label.Mui-disabled": {
                  color: theme.palette.primary.greySecound,
                },
              }}
            />
          );
        })}
      </RadioGroup>
    </Box>
  );
};

const isValidIPv4 = (value) => {
  const ipv4Pattern =
    /^(25[0-5]|2[0-4]\d|1\d{2}|[1-9]?\d)(\.(25[0-5]|2[0-4]\d|1\d{2}|[1-9]?\d)){3}$/;

  return ipv4Pattern.test(value);
};

const WiredBasic = ({ showEditGeneral }) => {
  const theme = useTheme();

  const { dataMap, getData, sendRequest } = useDataSourceManager();

  const [wiredConfigUuid] = useState(() => uuidv4());

  const [wiredConfigData, setWiredConfigData] = useState(null);

  const [originalWiredConfigData, setOriginalWiredConfigData] = useState(null);

  const breadcrumbs = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("selectBreadcrumbs") || "{}");
    } catch (error) {
      console.error("Failed to parse selectBreadcrumbs:", error);

      return {};
    }
  }, []);

  const hasManageOrg = breadcrumbs.realm?.hasManageOrg === true;

  const realmId = breadcrumbs.realm?.id ?? null;
  const orgsId = breadcrumbs.orgs?.id ?? null;
  const siteId = breadcrumbs.site?.id ?? null;

  const entId = hasManageOrg ? realmId : null;
  const orgId = hasManageOrg ? orgsId : realmId;

  const getWiredConfig = useCallback(() => {
    if (!orgId || !siteId) {
      console.error("Missing orgId or siteId.");
      return;
    }

    const resourcePath = hasManageOrg
      ? "/ents/:entId/orgs/:orgId/sites/:siteId/settings/wiredConfig.read"
      : "/orgs/:orgId/sites/:siteId/settings/wiredConfig.read";

    const restfulParams = hasManageOrg
      ? {
          entId,
          orgId,
          siteId,
        }
      : {
          orgId,
          siteId,
        };

    sendRequest(resourcePath, wiredConfigUuid, restfulParams);
  }, [entId, hasManageOrg, orgId, sendRequest, siteId, wiredConfigUuid]);

  useEffect(() => {
    getWiredConfig();
  }, [getWiredConfig]);

  const igmpVlanIsValid =
    !wiredConfigData?.igmpSnoopingState ||
    validateIgmpVlan(wiredConfigData?.igmpVlan ?? "");

  useEffect(() => {
    const wiredConfigResponse = getData(wiredConfigUuid);

    if (!wiredConfigResponse?.data?.length) {
      return;
    }

    const responseData = wiredConfigResponse.data[0];

    const formattedData = {
      ...responseData,

      stpState: Boolean(responseData.stpState),

      igmpSnoopingState: Boolean(responseData.igmpSnoopingState),

      igmpVlan: responseData.igmpVlan ?? "",

      dhcpState: Boolean(responseData.dhcpState),

      allowedServerIp: responseData.allowedServerIp || "",

      jumboFrameState: Boolean(responseData.jumboFrameState),

      LBDState: Boolean(responseData.LBDState),

      DDPState: Boolean(responseData.DDPState),
    };
    setWiredConfigData(formattedData);

    setOriginalWiredConfigData(formattedData);
  }, [dataMap, getData, wiredConfigUuid]);

  const updateWiredConfig = (updatedData) => {
    const resourcePath = hasManageOrg
      ? "/ents/:entId/orgs/:orgId/sites/:siteId/settings/wiredConfig.update"
      : "/orgs/:orgId/sites/:siteId/settings/wiredConfig.update";

    const restfulParams = hasManageOrg
      ? {
          entId,
          orgId,
          siteId,
        }
      : {
          orgId,
          siteId,
        };

    const requestParams = {
      updates: {
        "wired.basic": {
          stpState: Boolean(updatedData.stpState),

          igmpSnoopingState: Boolean(updatedData.igmpSnoopingState),

          igmpVlan: updatedData.igmpSnoopingState
            ? updatedData.igmpVlan.trim()
            : "",

          dhcpState: Boolean(updatedData.dhcpState),

          allowedServerIp: updatedData.allowedServerIp?.trim() || "",

          jumboFrameState: Boolean(updatedData.jumboFrameState),

          LBDState: Boolean(updatedData.LBDState),

          DDPState: Boolean(updatedData.DDPState),
        },
      },
    };
    sendRequest(resourcePath, uuidv4(), restfulParams, requestParams);
  };

  const handleConfigChange = (key, newValue) => {
    setWiredConfigData((previousData) => {
      if (!previousData) {
        return previousData;
      }

      return {
        ...previousData,
        [key]: newValue,
      };
    });
  };

  const handleAllowedServerIpChange = (value) => {
    setWiredConfigData((previousData) => {
      if (!previousData) {
        return previousData;
      }

      return {
        ...previousData,
        allowedServerIp: value,
      };
    });
  };

  const handleCancel = () => {
    if (!originalWiredConfigData) {
      return;
    }

    setWiredConfigData({
      ...originalWiredConfigData,
    });
  };

  const allowedServerIp = wiredConfigData?.allowedServerIp || "";

  const trimmedAllowedServerIp = allowedServerIp.trim();

  const allowedServerIpIsValid =
    !wiredConfigData?.dhcpState ||
    (trimmedAllowedServerIp !== "" && isValidIPv4(trimmedAllowedServerIp));

  const hasChanges = useMemo(() => {
    if (!wiredConfigData || !originalWiredConfigData) {
      return false;
    }

    return (
      JSON.stringify(wiredConfigData) !==
      JSON.stringify(originalWiredConfigData)
    );
  }, [wiredConfigData, originalWiredConfigData]);

  const handleSave = () => {
    if (!wiredConfigData) {
      return;
    }

    if (!allowedServerIpIsValid) {
      return;
    }

    if (!igmpVlanIsValid) {
      return;
    }

    const updatedData = {
      ...wiredConfigData,

      allowedServerIp: wiredConfigData.allowedServerIp?.trim() || "",

      igmpVlan: wiredConfigData.igmpSnoopingState
        ? wiredConfigData.igmpVlan?.trim() || ""
        : "",
    };

    updateWiredConfig(updatedData);

    setWiredConfigData(updatedData);

    setOriginalWiredConfigData(updatedData);
  };

  if (!wiredConfigData) {
    return null;
  }

  return (
    <Box
      sx={{
        padding: "20px 30px",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Typography
          sx={{
            color: theme.palette.primary.main,
            fontSize: theme.typography.customFontSize[5],
            fontWeight: theme.typography.customFontWeight[1],
          }}
        >
          Global Settings
        </Typography>

        {showEditGeneral && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
            }}
          >
            <OutlineRectangleButton
              onClick={handleCancel}
              disabled={!hasChanges}
              width="100%"
              height="32px"
            >
              Cancel
            </OutlineRectangleButton>

            <SolidButton
              onClick={handleSave}
              disabled={
                !hasChanges || !allowedServerIpIsValid || !igmpVlanIsValid
              }
              width="100%"
              height="32px"
            >
              Save
            </SolidButton>
          </Box>
        )}
      </Box>

      <Divider
        sx={{
          margin: "20px 0",
        }}
      />

      <ConfigRow
        showEditGeneral={showEditGeneral}
        label="RSTP"
        value={wiredConfigData.stpState}
        onChange={(value) => {
          handleConfigChange("stpState", value);
        }}
      />

      <ConfigRow
        showEditGeneral={showEditGeneral}
        label="IGMP Snooping"
        value={wiredConfigData.igmpSnoopingState}
        onChange={(value) => {
          handleConfigChange("igmpSnoopingState", value);
        }}
      />

      {wiredConfigData.igmpSnoopingState && (
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            mt: 2,
          }}
        >
          <Typography
            sx={{
              width: "200px",
              paddingLeft: "12px",
              paddingTop: "10px",
              boxSizing: "border-box",
              flexShrink: 0,
              color: theme.palette.primary.greyPrimary,
              fontSize: theme.typography.customFontSize[2],
              fontWeight: theme.typography.customFontWeight[1],
            }}
          >
            VLAN
          </Typography>

          <Box sx={{ width: "320px" }}>
            <InputField
              value={wiredConfigData.igmpVlan ?? ""}
              onChange={(value) => {
                if (/^[0-9,\-\s]*$/.test(value)) {
                  handleConfigChange("igmpVlan", value);
                }
              }}
              disabled={!showEditGeneral}
              maxLength={100}
              isValid={igmpVlanIsValid}
              errorHelperText="VLAN must be between 1 and 4094. Use ',' for multiple VLANs and '-' for ranges."
            />
          </Box>
        </Box>
      )}
      <ConfigRow
        showEditGeneral={showEditGeneral}
        label="DHCP Server Screen"
        value={wiredConfigData.dhcpState}
        onChange={(value) => {
          handleConfigChange("dhcpState", value);
        }}
      />

      {wiredConfigData.dhcpState && (
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            mt: 2,
          }}
        >
          <Typography
            sx={{
              width: "200px",
              paddingLeft: "12px",
              paddingTop: "10px",
              boxSizing: "border-box",
              flexShrink: 0,
              color: theme.palette.primary.greyPrimary,
              fontSize: theme.typography.customFontSize[2],
              fontWeight: theme.typography.customFontWeight[1],
            }}
          >
            Allowed (Trusted) Server IP
          </Typography>

          <InputField
            value={allowedServerIp}
            onChange={handleAllowedServerIpChange}
            disabled={!showEditGeneral}
            maxLength={15}
            width="320px"
            placeholder="192.168.1.1"
            isValid={allowedServerIpIsValid}
            errorHelperText="Please enter a valid IPv4 address manually."
          />
        </Box>
      )}

      <ConfigRow
        showEditGeneral={showEditGeneral}
        label="Jumbo Frame"
        value={wiredConfigData.jumboFrameState}
        onChange={(value) => {
          handleConfigChange("jumboFrameState", value);
        }}
      />

      <ConfigRow
        showEditGeneral={showEditGeneral}
        label="LBD"
        value={wiredConfigData.LBDState}
        onChange={(value) => {
          handleConfigChange("LBDState", value);
        }}
      />

      <ConfigRow
        showEditGeneral={showEditGeneral}
        label="DDP"
        value={wiredConfigData.DDPState}
        onChange={(value) => {
          handleConfigChange("DDPState", value);
        }}
      />
    </Box>
  );
};

export default WiredBasic;
